import { debugError } from '../utils/debug';

/**
 * Registry of page-local "flush my pending debounced edit into context" callbacks.
 *
 * Several wizard pages (BudgetPage, NetWorthPage) hold in-progress edits in local state
 * behind their own short debounce (e.g. BudgetPage's shared 1000ms expense-group timer)
 * before ever pushing to `SimpleCaseContext` / marking `BackgroundSaveService` dirty. A
 * page registers a flusher here on mount; anything that needs a save to reflect the true
 * on-screen state — `BackgroundSaveService.flush()`, and therefore `forceSave`, the
 * navigation guard, `triggerMerge`, the manual Save FAB, and the pre-download save — drains
 * every registered flusher first.
 *
 * No React imports here deliberately, to keep `backgroundSaveService.ts` (a plain
 * singleton, not a hook) free of any React dependency.
 *
 * See docs/AUTOSAVE_DETAILS.md sections 8, 10, 11.
 */

type Flusher = () => void;

const flushers = new Set<Flusher>();

/** Register a page-local flush callback. Returns an unregister function — call it on unmount. */
export function registerPendingLocalEditsFlusher(fn: Flusher): () => void {
    flushers.add(fn);
    return () => {
        flushers.delete(fn);
    };
}

/**
 * Runs every registered flusher. Each is isolated in a try/catch so one page's failure
 * can't block another's, or block the save that triggered this call.
 *
 * Returns true if at least one flusher ran (a caller may need to yield a tick for the
 * resulting setState to commit before reading the latest snapshot — see
 * `flushPendingLocalEditsAndWait`).
 */
export function flushPendingLocalEdits(): boolean {
    if (flushers.size === 0) {
        return false;
    }
    for (const fn of Array.from(flushers)) {
        try {
            fn();
        } catch (err) {
            debugError('pendingLocalEditsRegistry: flusher threw', err);
        }
    }
    return true;
}

/**
 * `flushPendingLocalEdits`, then — if anything ran — yields one macrotask so React can
 * commit the resulting setState(s). Each flusher pushes through `updateNestedField`/
 * `updateNestedFieldCallback` (both async: they await a `getCalcs()` microtask before
 * calling `setFormState`), so a caller that needs to read the provider's post-flush state
 * (directly, or via another `updateNestedField*` call whose `mergedCase`/`prev.data`
 * argument must include the flushed edit) should await this first.
 */
export async function flushPendingLocalEditsAndWait(): Promise<void> {
    if (flushPendingLocalEdits()) {
        await new Promise<void>(resolve => setTimeout(resolve, 0));
    }
}
